import { useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useStatsContext } from "contexts/StatsContext";
import VLToastComponent from "views/components/VLToastComponent";
import { composeApiURL } from "../../helpers/utility/axiosHelpers";

export interface CycleData {
  flow: { id: string; flow_id: string };
  flow_id: string;
  status: "PAUSED_FOR_REVIEW";
  text_result: string;
}

export const useFlywheelCycle = () => {
  const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;
  const { resetNeedsReviewText } = useStatsContext();

  const runCycle = useCallback(
    async (datasetId: string, flowId: string) => {
      try {
        const url = composeApiURL(
          HOST_URL,
          `/flywheel/${datasetId}/cycle/${flowId}`
        );
        const { data } = await axios.get<CycleData>(url);
        return data;
      } catch (err) {
        console.error("Failed to run flywheel cycle", err);
        throw err;
      }
    },
    [HOST_URL]
  );

  const finishCycle = useCallback(
    async (datasetId: string, flowId: string) => {
      try {
        const { data } = await axios.put<CycleData>(
          `/api/v1/flywheel/${datasetId}/apply/${flowId}`
        );
        return data;
      } catch (err) {
        console.error("Failed to finish flywheel cycle", err);
        throw err;
      } finally {
        resetNeedsReviewText();
      }
    },
    [resetNeedsReviewText]
  );

  const resetCycle = useCallback(
    async (datasetId: string, flowId: string) => {
      try {
        const { data } = await axios.delete<CycleData>(
          `/api/v1/flywheel/${datasetId}/abort/${flowId}`
        );
        return data;
      } catch (err) {
        console.error("Failed to reset flywheel cycle", err);
        const toastId = toast(
          <VLToastComponent
            title="Finish cycle"
            status="ERROR"
            description={`Something went wrong ${(err as Error).message}`}
            actionButton={{
              text: "Decline",
              type: "primary",
              onClick: async () => {
                toast.dismiss(toastId);
              },
            }}
            onClose={() => toast.dismiss(toastId)}
          />,
          { autoClose: 7000, closeButton: false, closeOnClick: false }
        );
      } finally {
        resetNeedsReviewText();
      }
    },
    [resetNeedsReviewText]
  );

  return { runCycle, finishCycle, resetCycle };
};
